/**
 * CSV und Druckansicht.
 *
 * Beides aus dem Entwurf übernommen, für deutsche Verhältnisse angepasst:
 * Semikolon statt Komma und Dezimalkomma, weil Excel in deutscher Locale
 * eine komma-getrennte Datei in eine einzige Spalte kippt.
 */

import {
  breakMs, de, dowLabel, fmtDecimalHours, fmtDuration, fmtSigned, fullDateLabel,
  hhmm, longDateLabel, monthLabel, overtimeBalanceMs, settledNetMs,
  type DayEntry, type DayKey, type Employee,
} from '@zeit/domain';

const e = de.export;

export interface ExportRow {
  employee: string;
  date: DayKey;
  dow: string;
  in: string;
  out: string;
  pause: string;
  net: string;
  netHours: string;
  status: string;
  note: string;
}

const STATUS_LABEL: Record<string, string> = {
  open: 'offen',
  closed: 'abgeschlossen',
  missing_out: 'Gehen fehlt',
  leave: 'Urlaub',
  sick: 'Krank',
};

export function buildRows(
  employees: Employee[],
  entriesByEmployee: Map<string, DayEntry[]>,
  keys: DayKey[],
  now: number,
): ExportRow[] {
  const rows: ExportRow[] = [];
  const wanted = new Set(keys);

  for (const emp of employees) {
    const entries = (entriesByEmployee.get(emp.$id!) ?? [])
      .filter((x) => wanted.has(x.date) && x.clockIn)
      .sort((a, b) => a.date.localeCompare(b.date));

    for (const entry of entries) {
      rows.push({
        employee: emp.name,
        date: entry.date,
        dow: dowLabel(entry.date),
        in: entry.clockIn ? hhmm(entry.clockIn) : '—',
        out: entry.clockOut ? hhmm(entry.clockOut) : '—',
        pause: fmtDuration(breakMs(entry, now)),
        net: fmtDuration(settledNetMs(entry, now)),
        netHours: fmtDecimalHours(settledNetMs(entry, now)),
        status: STATUS_LABEL[entry.status] ?? entry.status,
        note: entry.corrected ? 'korrigiert' : entry.note,
      });
    }
  }
  return rows;
}

/**
 * CSV mit BOM: ohne das Byte-Order-Mark liest Excel die Datei als Latin-1
 * und macht aus „Müller“ ein „MÃ¼ller“.
 */
export function downloadCsv(rows: ExportRow[], filename: string): void {
  const escape = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
  const lines = [
    e.csvHeaders.join(';'),
    ...rows.map((r) =>
      [r.employee, fullDateLabel(r.date), r.dow, r.in, r.out, r.pause, r.net, r.netHours, r.status, r.note]
        .map(escape)
        .join(';'),
    ),
  ];

  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function periodLabel(range: 'week' | 'month', keys: DayKey[]): string {
  if (range === 'month') return `${monthLabel(keys[0])} ${keys[0].slice(0, 4)}`;
  return `${fullDateLabel(keys[0])} – ${fullDateLabel(keys[keys.length - 1])}`;
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Druckansicht als eigenständiges Dokument in einem neuen Fenster.
 *
 * Absichtlich kein `window.print()` auf der Anwendung selbst: der Nachweis
 * soll ohne Navigation, Schaltflächen und Farbflächen auf Papier landen.
 */
export function openPrintView(
  employees: Employee[],
  entriesByEmployee: Map<string, DayEntry[]>,
  keys: DayKey[],
  range: 'week' | 'month',
  now: number,
  today: DayKey,
): boolean {
  const win = window.open('', '_blank');
  if (!win) return false;

  const period = periodLabel(range, keys);
  const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const sections = employees
    .map((emp) => {
      const rows = buildRows([emp], entriesByEmployee, keys, now);
      const wanted = new Set(keys);
      const total = (entriesByEmployee.get(emp.$id!) ?? [])
        .filter((x) => wanted.has(x.date))
        .reduce((sum, x) => sum + settledNetMs(x, now), 0);
      const target = range === 'week' ? emp.weeklyTargetHours * 3600000 : null;
      const balance = overtimeBalanceMs(emp, entriesByEmployee.get(emp.$id!) ?? [], now, today);

      const body = rows.length
        ? rows
            .map(
              (r) =>
                `<tr><td>${esc(fullDateLabel(r.date))}</td><td>${esc(r.dow)}</td><td>${esc(r.in)}</td>` +
                `<td>${esc(r.out)}</td><td>${esc(r.pause)}</td><td>${esc(r.net)}</td><td>${esc(r.note)}</td></tr>`,
            )
            .join('')
        : `<tr><td colspan="7">${esc(e.noRecords)}</td></tr>`;

      return (
        `<section><h2>${esc(emp.name)}${emp.department ? ` — ${esc(emp.department)}` : ''}</h2>` +
        '<table><thead><tr>' +
        `<th>${e.csvHeaders[1]}</th><th>${e.csvHeaders[2]}</th><th>${e.csvHeaders[3]}</th>` +
        `<th>${e.csvHeaders[4]}</th><th>${e.csvHeaders[5]}</th><th>${e.csvHeaders[6]}</th><th>${e.csvHeaders[9]}</th>` +
        `</tr></thead><tbody>${body}</tbody></table>` +
        `<p class="tot">${e.totalNet} <b>${fmtDuration(total)}</b>` +
        (target ? ` · ${e.weekTarget} <b>${fmtDuration(target)}</b> · ${e.difference} <b>${fmtSigned(total - target)}</b>` : '') +
        ` · ${e.balanceCarried} <b>${fmtSigned(balance)}</b></p>` +
        '<p class="sig">Datum, Unterschrift Mitarbeiter</p></section>'
      );
    })
    .join('');

  const html =
    '<!doctype html><html lang="de"><head><meta charset="utf-8">' +
    `<title>${esc(e.printTitle(period))}</title><style>` +
    '@page{margin:18mm}' +
    'body{font:12pt/1.5 Archivo,Helvetica,Arial,sans-serif;color:#10202e;margin:0}' +
    'h1{font-size:22pt;margin:0 0 2mm;letter-spacing:-.02em}' +
    '.meta{font-size:10pt;color:#5f707f;border-bottom:2px solid #10202e;padding-bottom:3mm;margin-bottom:6mm}' +
    'h2{font-size:14pt;margin:0 0 3mm}' +
    'section{margin-bottom:9mm;break-inside:avoid}' +
    'table{width:100%;border-collapse:collapse;font-size:11pt}' +
    'th{text-align:left;font-size:9pt;letter-spacing:.08em;text-transform:uppercase;border-bottom:2px solid #10202e;padding:2mm 2mm 1.5mm}' +
    'td{padding:1.8mm 2mm;border-bottom:1px solid #c5d3e1;font-variant-numeric:tabular-nums}' +
    '.tot{font-size:11pt;margin:3mm 0 0}' +
    '.sig{margin-top:10mm;font-size:9pt;color:#5f707f;border-top:1px solid #10202e;padding-top:1.5mm;width:70mm}' +
    '.legal{margin-top:8mm;font-size:8.5pt;color:#5f707f}' +
    `</style></head><body><h1>${esc(e.printTitle(period))}</h1>` +
    `<div class="meta">${esc(e.generated(longDateLabel(today)))}</div>` +
    sections +
    `<p class="legal">Aufzeichnung nach § 16 Abs. 2 ArbZG. Aufbewahrungsfrist zwei Jahre.</p>` +
    '</body></html>';

  win.document.write(html);
  win.document.close();
  setTimeout(() => {
    try {
      win.focus();
      win.print();
    } catch {
      /* Der Nutzer kann immer noch selbst drucken. */
    }
  }, 400);
  return true;
}
